import React from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent } from '../components';
import { RefreshCw, ExternalLink, Settings, Check, AlertCircle, XCircle } from 'lucide-react';

export const SourcesPage: React.FC = () => {
  const sources = [
    {
      id: '1',
      name: 'BrasilAPI',
      category: 'Dados Públicos',
      status: 'operacional' as const,
      documentation: 'https://brasilapi.com.br/docs',
      lastTest: new Date(),
      authRequired: false,
    },
    {
      id: '2',
      name: 'DataJud - CNJ',
      category: 'Processos Judiciais',
      status: 'nao_configurado' as const,
      documentation: 'https://datajud-wiki.cnj.jus.br/api-publica/',
      lastTest: null,
      authRequired: true,
    },
    {
      id: '3',
      name: 'Portal da Transparência',
      category: 'Dados Governamentais',
      status: 'nao_configurado' as const,
      documentation: 'https://portaldatransparencia.gov.br/api-de-dados',
      lastTest: null,
      authRequired: true,
    },
    {
      id: '4',
      name: 'crt.sh',
      category: 'Certificados SSL',
      status: 'operacional' as const,
      documentation: 'https://crt.sh/',
      lastTest: new Date(),
      authRequired: false,
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operacional':
        return <Check className="w-5 h-5 text-green-500" />;
      case 'nao_configurado':
        return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'indisponivel':
      case 'erro':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operacional':
        return 'Operacional';
      case 'nao_configurado':
        return 'Não Configurado';
      case 'indisponivel':
        return 'Indisponível';
      case 'erro':
        return 'Erro';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Fontes de Dados</h1>
        <p className="text-gray-400">Gerenciar integrações com serviços de pesquisa</p>
      </div>

      {/* Lista de Fontes */}
      <div className="space-y-4">
        {sources.map((source) => (
          <Card key={source.id}>
            <CardContent className="py-6">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{source.name}</h3>
                    <span className="text-xs px-2 py-1 rounded bg-gray-800 text-gray-300">
                      {source.category}
                    </span>
                    <div className="flex items-center gap-1">
                      {getStatusIcon(source.status)}
                      <span className="text-xs text-gray-400">{getStatusText(source.status)}</span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-400 mb-3">
                    Autenticação: {source.authRequired ? 'Obrigatória' : 'Não requerida'}
                  </p>

                  {source.lastTest && (
                    <p className="text-xs text-gray-500">
                      Último teste: {source.lastTest.toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 flex-col">
                  {source.status === 'nao_configurado' && (
                    <Button variant="secondary" size="sm">
                      <Settings className="w-4 h-4 mr-2" />
                      Configurar
                    </Button>
                  )}

                  {source.status === 'operacional' && (
                    <Button variant="secondary" size="sm">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Testar
                    </Button>
                  )}

                  <a
                    href={source.documentation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="w-4 h-4" />
                      Docs
                    </Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Informações Adicionais */}
      <Card>
        <CardHeader>
          <CardTitle>Sobre as Fontes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-300">
          <p>
            Todas as consultas são feitas através de APIs oficiais e documentadas. Os dados são
            normalizados e armazenados de forma segura, respeitando as políticas de privacidade e
            conformidade com a LGPD.
          </p>
          <p>
            Você pode ativar ou desativar fontes individuais conforme necessário. As fontes não
            configuradas requerem credenciais válidas que devem ser obtidas junto aos provedores
            oficiais.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
